import { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import useLogActivityAccess from '../hooks/useLogActivityAccess';

const S = {
  page: { minHeight: 'calc(100vh - 66px)', background: 'linear-gradient(160deg,#0a1628 0%,#1a2a4a 100%)', padding: '32px 20px', fontFamily: 'Poppins,sans-serif' },
  inner: { maxWidth: 960, margin: '0 auto' },
  title: { color: '#fff', fontSize: 'clamp(1.5rem,4vw,2.2rem)', fontWeight: 800, textAlign: 'center', marginBottom: 6 },
  subtitle: { color: 'rgba(255,255,255,0.55)', textAlign: 'center', fontSize: '0.95rem', marginBottom: 28 },
  tabs: { display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 20, flexWrap: 'wrap' },
  tab: (active) => ({ padding: '9px 26px', borderRadius: 30, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.92rem', fontFamily: 'Poppins,sans-serif', background: active ? '#1e90ff' : 'rgba(255,255,255,0.1)', color: active ? '#fff' : 'rgba(255,255,255,0.65)', transition: 'all 0.2s' }),
  search: { display: 'block', width: '100%', maxWidth: 380, margin: '0 auto 28px', padding: '11px 18px', borderRadius: 30, border: 'none', background: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: '0.95rem', outline: 'none', fontFamily: 'Poppins,sans-serif' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 18 },
  card: { background: 'rgba(255,255,255,0.06)', borderRadius: 16, border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden', cursor: 'pointer', transition: 'transform 0.2s,box-shadow 0.2s' },
  cardHead: (s) => ({
    background: s === 'separable'
      ? 'linear-gradient(135deg,#1e90ff,#0052cc)'
      : s === 'inseparable'
        ? 'linear-gradient(135deg,#e67e22,#c0392b)'
        : 'linear-gradient(135deg,#9b59b6,#6c3483)',
    padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  }),
  cardVerbo: { color: '#fff', fontWeight: 800, fontSize: '1.3rem', margin: 0 },
  cardTrad: { color: 'rgba(255,255,255,0.75)', fontSize: '0.82rem', margin: 0, marginTop: 2 },
  badge: { background: 'rgba(255,255,255,0.25)', color: '#fff', borderRadius: 12, padding: '3px 10px', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' },
  cardBody: { padding: '14px 18px' },
  exRow: { marginBottom: 12 },
  fraseText: { color: 'rgba(255,255,255,0.55)', fontSize: '0.85rem', fontStyle: 'italic' },
  audioBtn: { background: 'rgba(30,144,255,0.3)', border: 'none', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', color: '#7ecfff', fontSize: '0.82rem' },
  empty: { textAlign: 'center', color: 'rgba(255,255,255,0.4)', marginTop: 60, fontSize: '1.1rem' },
};

function playTTS(text) {
  fetch('/synt/text-to-speech', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, languageCode: 'en-US', voiceName: 'en-US-Neural2-J', pitch: 0 }),
  }).then(r => r.blob()).then(blob => new Audio(URL.createObjectURL(blob)).play()).catch(() => {});
}

async function traduzir(texto) {
  const r = await fetch('/traduzir/traduzir', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ texto, idiomaOrigem: 'en', idiomaDestino: 'pt' }),
  });
  const data = await r.json();
  return data.textoTraduzido || '';
}

function ExemploRow({ ex }) {
  const [trad, setTrad] = useState(null);
  const [loading, setLoading] = useState(false);

  async function toggleTrad() {
    if (trad !== null) { setTrad(null); return; }
    setLoading(true);
    try { setTrad(ex.frase_pt || await traduzir(ex.frase_en)); } catch { setTrad('Erro ao traduzir.'); }
    setLoading(false);
  }

  return (
    <div style={S.exRow}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={S.fraseText}>"{ex.frase_en}"</span>
        <button style={S.audioBtn} onClick={() => playTTS(ex.frase_en)}>🔊</button>
        <button
          style={{ ...S.audioBtn, background: trad !== null ? 'rgba(46,204,113,0.35)' : 'rgba(255,255,255,0.12)', color: trad !== null ? '#7effc0' : 'rgba(255,255,255,0.6)', fontSize: '0.75rem', padding: '3px 9px' }}
          onClick={toggleTrad}
          disabled={loading}
        >
          {loading ? '...' : trad !== null ? 'EN' : 'PT'}
        </button>
      </div>
      {trad !== null && (
        <p style={{ ...S.fraseText, color: '#7effc0', marginTop: 4 }}>"{trad}"</p>
      )}
    </div>
  );
}

function PhrasalCard({ pv }) {
  const [open, setOpen] = useState(false);
  const label = pv.separavel === 'separable' ? 'Separável' : pv.separavel === 'inseparable' ? 'Inseparável' : 'Ambos';

  return (
    <div
      style={S.card}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.3)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={S.cardHead(pv.separavel)} onClick={() => setOpen(o => !o)}>
        <div>
          <p style={S.cardVerbo}>{pv.verbo}</p>
          <p style={S.cardTrad}>{pv.traducao}</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          <span style={S.badge}>{label}</span>
          <button style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8, padding: '3px 10px', color: '#fff', cursor: 'pointer', fontSize: '0.8rem' }}
            onClick={e => { e.stopPropagation(); playTTS(pv.verbo); }}>
            🔊 Ouvir
          </button>
        </div>
      </div>

      {open && (
        <div style={S.cardBody}>
          {(!pv.exemplos || pv.exemplos.length === 0) && (
            <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.85rem', margin: 0 }}>Sem exemplos cadastrados.</p>
          )}
          {(pv.exemplos || []).map((ex, i) => <ExemploRow key={i} ex={ex} />)}
        </div>
      )}
    </div>
  );
}

export default function PhrasalVerbs() {
  useLogActivityAccess('phrasal-verbs');
  const [verbos, setVerbos] = useState([]);
  const [filtro, setFiltro] = useState('all');
  const [busca, setBusca] = useState('');
  const [loading, setLoading] = useState(true);
  const cacheRef = useRef({});

  useEffect(() => {
    const key = filtro;
    if (cacheRef.current[key]) { setVerbos(cacheRef.current[key]); setLoading(false); return; }
    setLoading(true);
    const url = filtro === 'all' ? '/phrasal-verbs' : `/phrasal-verbs?separavel=${filtro}`;
    api.get(url)
      .then(r => { cacheRef.current[key] = r.data; setVerbos(r.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filtro]);

  const filtrados = verbos.filter(v =>
    v.verbo.toLowerCase().includes(busca.toLowerCase()) ||
    v.traducao.toLowerCase().includes(busca.toLowerCase())
  );

  return (
    <div style={S.page}>
      <div style={S.inner}>
        <h1 style={S.title}>🔗 Phrasal Verbs</h1>
        <p style={S.subtitle}>Domine os phrasal verbs — verbos separáveis e inseparáveis com exemplos</p>

        <div style={S.tabs}>
          {[['all','Todos'],['separable','Separáveis'],['inseparable','Inseparáveis']].map(([k,l]) => (
            <button key={k} style={S.tab(filtro===k)} onClick={() => { setFiltro(k); setBusca(''); }}>{l}</button>
          ))}
        </div>

        <input
          style={S.search}
          placeholder="🔍 Buscar phrasal verb..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
        />

        {loading ? (
          <p style={S.empty}><i className="fas fa-spinner fa-spin" /> Carregando...</p>
        ) : filtrados.length === 0 ? (
          <p style={S.empty}>Nenhum phrasal verb encontrado.</p>
        ) : (
          <div style={S.grid}>
            {filtrados.map(v => <PhrasalCard key={v.id} pv={v} />)}
          </div>
        )}
      </div>
    </div>
  );
}
